(() => {
  const CYCLE_INTERVAL_MS = 2500;
  const boundWrappers = new WeakSet();

  const tick = (wrapper) => {
    const hoverImage = wrapper.querySelector('[data-product-card-hover-image]');
    if (!hoverImage || hoverImage.classList.contains('hidden')) {
      wrapper.removeAttribute('data-cycling-active');
      return;
    }
    wrapper.toggleAttribute('data-cycling-active');
  };

  const bindWrapper = (wrapper) => {
    if (boundWrappers.has(wrapper)) return;
    boundWrappers.add(wrapper);
    setInterval(() => tick(wrapper), CYCLE_INTERVAL_MS);
  };

  const bindAll = (root) => {
    root.querySelectorAll('[data-product-card-image-wrapper][data-cycle-enabled="true"]').forEach(bindWrapper);
  };

  document.addEventListener('DOMContentLoaded', () => {
    bindAll(document);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches('[data-product-card-image-wrapper][data-cycle-enabled="true"]')) bindWrapper(node);
          bindAll(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  });
})();

document.addEventListener('DOMContentLoaded', () => {
  if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

  // Groups a whole-number amount with commas every 3 digits (1884 → "1,884").
  // The card price is a computed integer with the ₹ symbol in a separate span,
  // so the Liquid `money` filter can't be applied on variant change.
  const formatThousands = (amount) => Number(amount).toLocaleString('en-US');

  // The BAU multiplier data attributes (data-bau-multiplier, data-bau-tier1-eligible,
  // data-bau-tier2-threshold-paise) are snapshotted at page-load time from the cart
  // total at that moment. If the cart total changes afterwards without a page reload
  // (e.g. added to cart from a different section), an already-rendered card would
  // otherwise keep showing its stale rate. bauCartTotalPaise is refreshed from the
  // live cart on every cartUpdate/cartAdd event and used instead of the page-load
  // snapshot whenever it's available.
  let bauCartTotalPaise = null;

  function bauComputeMultiplier(offerEl) {
    if (offerEl.dataset.bauIsCombo === 'true') return 0.75;

    // No live cart total yet (haven't seen a cart event this page view) — fall back
    // to the page-load-time value Liquid already computed correctly, regardless of
    // whether Tier 1 is eligible, since we have no live total to test it against.
    if (bauCartTotalPaise == null) {
      return parseFloat(offerEl.dataset.bauMultiplier) || 0.85;
    }

    const tier1Eligible = offerEl.dataset.bauTier1Eligible === 'true';
    const tier2ThresholdPaise = parseFloat(offerEl.dataset.bauTier2ThresholdPaise) || 0;

    if (tier1Eligible && bauCartTotalPaise < tier2ThresholdPaise) {
      return 0.9;
    }
    return 0.85;
  }

  function bauRenderOfferPrice(offerEl) {
    const bauPriceEl = offerEl.querySelector('[data-bau-offer-price]');
    if (!bauPriceEl) return;
    const basePrice = parseFloat(offerEl.dataset.bauBasePrice) || 0;
    const multiplier = bauComputeMultiplier(offerEl);
    bauPriceEl.textContent = formatThousands(Math.round((basePrice * multiplier) / 100));
  }

  function bauRefreshAllOfferPrices() {
    document.querySelectorAll('[data-bau-offer]').forEach(bauRenderOfferPrice);
  }

  // #bau-config is rendered by snippets/cart-footer.liquid inside the cart drawer
  // (part of the persistent header, present on virtually every page). It's kept
  // fresh by Shopify's own cart AJAX section-rendering — the exact same source
  // cart-mode-bau.js already reads for the cart drawer/page prices, which are
  // confirmed working correctly. Reading it directly here, synchronously, is more
  // reliable than a separate custom fetch: no network round-trip, no timing race,
  // and no browser-specific fetch-caching behavior to worry about at all.
  function bauReadConfigCartTotal() {
    const configEl = document.getElementById('bau-config');
    if (!configEl) return null;
    try {
      const config = JSON.parse(configEl.textContent);
      return typeof config.cart_total_paise === 'number' ? config.cart_total_paise : null;
    } catch (_) {
      return null;
    }
  }

  async function bauRefreshCartTotal() {
    const fromConfig = bauReadConfigCartTotal();
    if (fromConfig != null) {
      bauCartTotalPaise = fromConfig;
      bauRefreshAllOfferPrices();
      return;
    }

    // No cart drawer present on this page (or its config hasn't rendered yet) —
    // fall back to a direct fetch. cache: 'no-store' forces a real network
    // round-trip rather than risking a browser-cached response for a same-origin
    // GET when the server doesn't send explicit no-cache headers.
    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      if (!res.ok) return;
      const cart = await res.json();
      bauCartTotalPaise = cart.total_price;
      bauRefreshAllOfferPrices();
    } catch (_) {
      // Network hiccup — keep showing the last known-good rate rather than guessing.
    }
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, bauRefreshCartTotal);
  subscribe(PUB_SUB_EVENTS.cartAdd, bauRefreshCartTotal);

  // Confirm the live cart total immediately rather than only trusting the
  // page-load-time Liquid snapshot — covers any page-level caching (proxy/CDN,
  // or a browser serving an older cached copy of the HTML itself) independently
  // of the /cart.js fetch caching handled above.
  bauRefreshCartTotal();

  // iOS Safari uses the back-forward cache far more aggressively than desktop
  // Chrome: navigating back to this page (swipe-back, back button) very often
  // restores the exact frozen DOM/JS state from before, without re-running any
  // script or re-fetching anything. If the cart changed while this page was
  // frozen, the restored page would otherwise keep showing whatever rate was
  // last computed. event.persisted is true only on a bfcache restore (not on a
  // normal load, where this would just be a harmless duplicate of the call above).
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) bauRefreshCartTotal();
  });

  subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
    const { productId, variant } = data;
    if (!productId || !variant) return;

    const cards = document.querySelectorAll(`[data-product-card][data-product-id="${productId}"]`);
    if (!cards.length) return;

    cards.forEach((card) => {
      const image = card.querySelector('[data-product-card-image]');
      let hoverImage = card.querySelector('[data-product-card-hover-image]');
      const titleLink = card.querySelector('[data-product-card-title-link]');
      const priceEl = card.querySelector('[data-product-card-price]');
      const comparePriceEl = card.querySelector('[data-product-card-compare-price]');
      const discountPctEl = card.querySelector('[data-product-card-discount-pct]');

      if (image && variant.image) {
        const url300 = variant.image.replace(/width=\d+/, 'width=300');
        const url600 = variant.image.replace(/width=\d+/, 'width=600');
        image.src = url600;
        image.srcset = `${url300} 300w, ${url600} 600w`;
      }

      if (variant.hover_image) {
        if (!hoverImage && image) {
          hoverImage = document.createElement('img');
          hoverImage.alt = '';
          hoverImage.setAttribute('aria-hidden', 'true');
          hoverImage.loading = 'lazy';
          hoverImage.className = 'product-card__image product-card__image--hover';
          hoverImage.setAttribute('data-product-card-hover-image', '');
          image.insertAdjacentElement('afterend', hoverImage);
        }
        if (hoverImage) {
          hoverImage.src = variant.hover_image;
          hoverImage.classList.remove('hidden');
        }
      } else if (hoverImage) {
        hoverImage.classList.add('hidden');
      }

      if (titleLink) {
        const url = new URL(titleLink.href);
        url.searchParams.set('variant', variant.id);
        titleLink.href = url.toString();
      }

      const vdDiscountPaise = Number(variant.vd_discount_paise || 0);

      if (priceEl) {
        const integerEl = priceEl.querySelector('[data-price-integer]');
        const displayPrice = vdDiscountPaise > 0 ? Math.max(variant.price - vdDiscountPaise, 0) : variant.price;
        if (integerEl) {
          integerEl.textContent = formatThousands(Math.round(displayPrice / 100));
        } else {
          priceEl.textContent = variant.price_formatted;
        }
      }

      if (comparePriceEl) {
        if (vdDiscountPaise > 0) {
          comparePriceEl.textContent = `₹${formatThousands(Math.round(variant.price / 100))}`;
          comparePriceEl.classList.remove('hidden');
        } else if (variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = `₹${formatThousands(Math.round(variant.compare_at_price / 100))}`;
          comparePriceEl.classList.remove('hidden');
        } else {
          comparePriceEl.classList.add('hidden');
        }
      }

      if (discountPctEl) {
        if (vdDiscountPaise > 0) {
          discountPctEl.textContent = `(${variant.vd_value}% OFF)`;
          discountPctEl.classList.remove('hidden');
        } else if (variant.compare_at_price > variant.price) {
          const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          discountPctEl.textContent = `(${pct}% OFF)`;
          discountPctEl.classList.remove('hidden');
        } else {
          discountPctEl.classList.add('hidden');
        }
      }

      const variantOfferEl = card.querySelector('[data-variant-offer]');
      if (variantOfferEl) {
        const offerTextEl = variantOfferEl.querySelector('[data-product-card-offer-text]');
        const offerMap = variantOfferEl.dataset.variantOfferMap || '';
        const defaultText = variantOfferEl.dataset.variantOfferDefaultText || '';

        let matchedText = '';
        offerMap.split(',').forEach((pair) => {
          const trimmedPair = pair.trim();
          const separatorIndex = trimmedPair.indexOf(':');
          if (separatorIndex === -1) return;
          const pairVariantId = trimmedPair.slice(0, separatorIndex).trim();
          if (pairVariantId === String(variant.id)) {
            matchedText = trimmedPair.slice(separatorIndex + 1).trim();
          }
        });

        if (matchedText) {
          if (offerTextEl) offerTextEl.textContent = matchedText;
          variantOfferEl.classList.remove('hidden');
        } else {
          if (offerTextEl) offerTextEl.innerHTML = defaultText;
          variantOfferEl.classList.toggle('hidden', defaultText.trim() === '');
        }
      }

      const bauOfferEl = card.querySelector('[data-bau-offer]');
      if (bauOfferEl) {
        // Keep the base price in sync with whichever variant is now selected, so a
        // later cart-total-triggered refresh (bauRefreshAllOfferPrices) recomputes
        // off the right price instead of the variant that was selected at page load.
        bauOfferEl.dataset.bauBasePrice = String(variant.price);
        bauRenderOfferPrice(bauOfferEl);
      }

      const atcBtn = card.querySelector('[data-product-card-atc]');
      if (atcBtn) {
        const isAvailable = variant.available !== false;
        const productAtcTitle = atcBtn.closest('product-form')?.dataset.productAtcTitle?.trim() ?? '';

        // A custom ATC title worded like "sold out" contradicts a continue-selling
        // variant, which is still purchasable — ignore it and fall back to the
        // normal Add to cart / Sold out label instead of misleading copy.
        const resolvedAtcTitle = (variant.atc_title?.trim() || productAtcTitle).trim();
        const isSoldOutWorded = ['sold out', 'sold-out', 'soldout'].includes(resolvedAtcTitle.toLowerCase());
        const useCustomTitle = Boolean(productAtcTitle) && variant.inventory_policy === 'continue' && !isSoldOutWorded;

        const isDisabled = useCustomTitle ? false : !isAvailable;
        atcBtn.disabled = isDisabled;
        atcBtn.setAttribute('aria-disabled', String(isDisabled));

        const text = useCustomTitle
          ? resolvedAtcTitle
          : (isAvailable ? atcBtn.dataset.addToCartText : atcBtn.dataset.soldOutText);
        const textEl = atcBtn.querySelector('[data-button-text]');
        if (text && textEl) textEl.textContent = text;
      }
    });
  });
});
